import React, { useState } from 'react'
import clsx from 'clsx'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  ButtonBase,
  CircularProgress,
  Link as MuiLink,
  Typography,
} from '@material-ui/core'
import { Link } from '@src/components/Link'
import {
  productDetailRoute,
  staticContent,
  CartType,
  useRouter,
} from '@src/routes'
import { QuickCartQuery } from '@src/queries/QuickCartQuery.generated'
import { CartItemType, EmproveItemType } from '@src/types/graphql-types'
import { QuickCartItemFragment } from '@src/queries/AllCartsQuery.generated'
import { useUserSession } from '@utils/useUserSession'
import { handleGARemoveFromCart } from '@utils/analytics/cartAndCheckoutEvents'
import { EventValues } from '@sial/common-utils'
import {
  getCartMiniAction,
  materialIdIsPromotionalBundle,
} from '@utils/cartUtils'
import { sendRemoveItemsFromCartEvent } from '@utils/tealiumAnalytics'
import HandleMarkup from '../HandleMarkup'
import CartItemConfigurationPopover from '../CartItemConfigurationPopover'
import ResponsiveCatalogImage from '@src/components/ResponsiveCatalogImage'
import ClearIcon from '@material-ui/icons/Clear'
import { useDeleteCartItem } from '@utils/useCart'
import { useRefetchSpecificFullCart } from '@utils/useRefetchSpecificFullCart'
import LiquidQuantityInput from '../LiquidQuantityInput'
import MaterialAvailabilityPreview from '@src/components/MaterialAvailabilityPreview/MaterialAvailabilityPreview'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import messages from '@utils/messages'
import { useDebounce } from 'react-use'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { TrackedAvailabilityMessageProvider } from '@src/components/TrackedAvailabilityMessage/TrackedAvailabilityMessageContext'
import SubscriptionFrequency from '@src/components/ProductLockup/SubscriptionFrequency'

const useStyles = makeStyles((theme: Theme) => ({
  item: {
    padding: theme.spacing(4),
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  contentLoading: {
    opacity: 0.5,
  },
  product: {
    display: 'flex',
    overflow: 'hidden',
    width: '100%',
  },
  imageWrapper: {
    width: 60,
    height: 60,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `solid 1px ${theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
    marginRight: theme.spacing(4),
    wordWrap: 'break-word',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
  details: {
    width: '100%',
  },
  materialNumber: {
    display: 'block',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    cursor: 'pointer',
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
  },
  materialName: {
    fontWeight: theme.typography.fontWeightBold,
  },
  materialDescription: {
    fontSize: theme.typography.pxToRem(12),
  },
  qty: {
    fontSize: theme.typography.pxToRem(12),
    marginTop: theme.spacing(1),
  },
  quantityInput: {
    height: theme.typography.pxToRem(34),
    maxWidth: theme.typography.pxToRem(105),
    marginTop: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(0),
    },
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(9, 0),
  },
  loadingIcon: {
    color: theme.palette.grey[300],
  },
  deleteCartItem: {
    fontWeight: theme.typography.fontWeightBold,
    marginRight: theme.spacing(1),
  },
  removeIcon: {
    fontSize: theme.typography.pxToRem(16),
    '&.disabled': {
      color: theme.palette.grey[400],
      opacity: 0.35,
    },
  },
  materialNumberWrapper: {
    display: 'flex',
  },
  marketplaceTitle: {
    fontWeight: theme.typography.fontWeightBold,
  },
  marketplaceName: {
    fontWeight: theme.typography.fontWeightBold,
    margin: theme.spacing(-4, -4, 4, -4),
    background: theme.palette.common.white,
    padding: theme.spacing(4, 0, 1, 4),
  },
  placeholderBrandName: {
    color: theme.palette.common.white,
  },
  badgeWrapper: {
    paddingBottom: theme.spacing(2),
  },
  availabilityContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  availabilityIcon: {
    marginRight: theme.spacing(2),
    fontSize: theme.typography.pxToRem(16),
  },
  quantityContainer: {
    alignItems: 'flex-end',
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(4),
    width: '100%',
  },
  productDetailsContainer: {
    width: '100%',
  },
  detailsContainer: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
    overflow: 'hidden',
    paddingBottom: theme.spacing(2),
  },
  netPrice: {
    fontSize: theme.typography.pxToRem(14),
  },
  price: {
    fontSize: theme.typography.pxToRem(16),
  },
  pricingContainer: {
    userSelect: 'none',
    display: 'flex',
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
  },
  listPrice: {
    color: theme.palette.grey[500],
    fontSize: theme.typography.pxToRem(16),
    marginRight: theme.spacing(2),
    textDecoration: 'line-through',
  },
  subscriptionFrequencyContainer: {
    marginTop: theme.spacing(1),
    minWidth: 150,
  },
}))

export interface MiniCarMenuItemProps {
  item: QuickCartItemFragment
  cart: QuickCartQuery['getCart']
  cartType?: CartType
  applyGroupedHeading?: boolean | null | undefined
  closeMiniCart: () => void
  handleUpdateQuantity: (itemId: string, value: number) => void
  priceData: any
  loading: boolean
  error: any
}

const MiniCartItem: React.FC<MiniCarMenuItemProps> = ({
  item,
  cart,
  cartType,
  applyGroupedHeading,
  closeMiniCart,
  handleUpdateQuantity,
  priceData,
  loading,
}) => {
  const router = useRouter()
  const classes = useStyles()
  const { isBlueErpIntegrationEnabled, isB2BUser, userErpType } =
    useCurrentUser()
  const refetchSpecificFullCart = useRefetchSpecificFullCart(cartType)
  const [deleteCartItem] = useDeleteCartItem()
  const {
    userSession: { language, country },
  } = useUserSession()

  const [isDeleting, setIsDeleting] = useState(false)
  const [confPopoverOpen, setConfPopoverOpen] = useState(false)
  const [quantity, setQuantity] = useState(item.quantity)

  const isPromotionalBundle = materialIdIsPromotionalBundle(
    item.material.number
  )
  const emproveIteType =
    item.type === CartItemType.Emprove &&
    item?.emproveDossierInfo?.dossierSubType
  const emproveItemNo =
    item.type === CartItemType.Emprove &&
    item?.emproveDossierInfo?.dossierMaterialNumber
  const imgSizes = '60px'

  const itemPriceAvailability = priceData[item.id]?.materialPriceAvailability
  const hidePrice = itemPriceAvailability?.hidePriceMessageKey

  // if 'primary' message type exists then return the availability, else return the first availability
  const availability =
    itemPriceAvailability?.availabilities?.find(
      ({ messageType }) => messageType === 'primary'
    ) ?? itemPriceAvailability?.availabilities[0]

  const showPricing =
    !availability?.displayInquireLink &&
    !item.promoCode &&
    !item.quoteNumber &&
    !hidePrice

  const handleDeleteCartItem = async () => {
    setIsDeleting(true)
    try {
      //todo remove action from deleteCartItem
      await deleteCartItem({
        variables: {
          cartItemId: item.id,
          action: getCartMiniAction(cartType),
          cartId: cart.id,
        },
      })
      //call cart API if the active path is cart
      const isCartPage = router.pathname === '/[country]/[language]/cart'
      if (isCartPage) {
        closeMiniCart()
        refetchSpecificFullCart()
      }
      const userType = { isBlueErpIntegrationEnabled, isB2BUser, userErpType }
      sendRemoveItemsFromCartEvent([item], userType)
      handleGARemoveFromCart(
        [item],
        EventValues.Slider,
        cartType === 'marketplacecart' ? 'marketplacecart' : undefined,
        itemPriceAvailability?.currency
      )
    } catch (_) {
      alert('Something went wrong')
      setIsDeleting(false)
    }
  }

  useDebounce(
    () => {
      if (quantity !== item.quantity) {
        handleUpdateQuantity(item.id, quantity)
      }
    },
    500,
    [quantity]
  )

  return (
    <div
      className={classes.item}
      data-testid="mini-cart-item"
      id="mini-cart-item"
    >
      {cartType === CartType.MarketplaceCart && applyGroupedHeading && (
        <div
          className={clsx(
            classes.marketplaceName,
            !item.brand &&
              !item?.material?.brand?.name &&
              item?.material?.displaySellerName &&
              classes.placeholderBrandName
          )}
        >
          {item?.material?.displaySellerName ||
            item.brand ||
            item?.material?.brand?.name ||
            'Placeholder'}
        </div>
      )}
      <div
        className={clsx(classes.content, isDeleting && classes.contentLoading)}
      >
        <div className={classes.product}>
          <div className={classes.imageWrapper}>
            <ResponsiveCatalogImage
              className={classes.image}
              alt={item.material.number}
              src={item.material.image.smallUrl}
              sizes={imgSizes}
              urls={[
                item.material.image.smallUrl,
                item.material.image.mediumUrl,
                item.material.image.largeUrl,
              ]}
            />
          </div>

          <div className={classes.productDetailsContainer}>
            <div className={classes.details}>
              <div className={classes.detailsContainer}>
                <div>
                  {isPromotionalBundle ? (
                    <span>
                      <strong>{item.material.number}</strong>
                    </span>
                  ) : item.type === CartItemType.Configurator ? (
                    <CartItemConfigurationPopover
                      id={
                        confPopoverOpen
                          ? `${item.material.number}-configuration`
                          : undefined
                      }
                      open={confPopoverOpen}
                      setOpen={setConfPopoverOpen}
                      materialNumber={item.material.number}
                      configuration={item.configuration}
                      placement="left-start"
                    >
                      <MuiLink
                        className={classes.materialNumber}
                        onClick={() => setConfPopoverOpen(!confPopoverOpen)}
                        color="secondary"
                      >
                        {item.material.number}
                      </MuiLink>
                    </CartItemConfigurationPopover>
                  ) : item.type === CartItemType.Emprove ? (
                    <Link
                      {...staticContent.index({
                        path: '/services/product-services/emprove-program',
                        language,
                        country,
                      })}
                      passHref
                    >
                      <MuiLink className={classes.materialNumber}>
                        {EmproveItemType.Single === emproveIteType
                          ? `${item.material.number}-${emproveItemNo}`
                          : `${item.material.number}-Subscription`}
                      </MuiLink>
                    </Link>
                  ) : item.material.name &&
                    (item.material.brand.key || item.material.brand.name) ? (
                    <Link
                      {...productDetailRoute.index(
                        (
                          item.material.brand.key ||
                          item.material.brand.name ||
                          ''
                        )
                          .replace(' ', '')
                          .toLowerCase(),
                        item.material.product || item.material.id.split('-')[0],
                        item.type === CartItemType.ThirdPartyProvider
                          ? { context: 'bbe' }
                          : {}
                      )}
                      passHref
                    >
                      <div className={classes.materialNumberWrapper}>
                        <MuiLink className={classes.materialNumber}>
                          {item.material.number}
                        </MuiLink>
                      </div>
                    </Link>
                  ) : (
                    <Typography variant="subtitle2">
                      {item.material.number}
                    </Typography>
                  )}
                  {item.material.name ? (
                    <div className={classes.materialName}>
                      <HandleMarkup value={item.material.name} />
                    </div>
                  ) : null}
                  {item.material.description && (
                    <div className={classes.materialDescription}>
                      <HandleMarkup value={item.material.description} />
                    </div>
                  )}
                </div>

                {isDeleting ? (
                  <CircularProgress
                    className={classes.loadingIcon}
                    size={22}
                    thickness={5}
                  />
                ) : (
                  <>
                    <ButtonBase
                      className={classes.deleteCartItem}
                      title="Remove from cart"
                      aria-label="Remove from cart"
                      data-testid="remove-from-cart-button"
                      id="remove-from-cart-button"
                      onClick={handleDeleteCartItem}
                    >
                      <ClearIcon className={classes.removeIcon} />
                    </ButtonBase>
                  </>
                )}
              </div>
            </div>

            <>
              <div className={classes.availabilityContainer}>
                {availability ? (
                  <TrackedAvailabilityMessageProvider
                    source="minicart"
                    brand={item.material.brand.key}
                    item={item.material.number}
                    waitUntilVisible
                    pricing={itemPriceAvailability}
                  >
                    <MaterialAvailabilityPreview
                      material={itemPriceAvailability}
                      canAddToCart
                      hideDetails
                      hidePlantLoc
                    />
                  </TrackedAvailabilityMessageProvider>
                ) : null}
              </div>

              {item.subscriptionFrequency && (
                <SubscriptionFrequency
                  cartItemId={item.id}
                  cartType={cartType}
                  subscriptionFrequency={item.subscriptionFrequency}
                  styles={classes.subscriptionFrequencyContainer}
                />
              )}

              {showPricing && itemPriceAvailability ? (
                <div
                  className={clsx(
                    classes.pricingContainer,
                    loading && classes.contentLoading
                  )}
                >
                  {!!itemPriceAvailability?.price &&
                  !!itemPriceAvailability?.listPrice &&
                  itemPriceAvailability?.listPrice >
                    itemPriceAvailability?.price ? (
                    <div className={classes.listPrice}>
                      <FormattedNumber
                        style="currency"
                        currencyDisplay="symbol"
                        currency={itemPriceAvailability?.currency}
                        value={itemPriceAvailability?.listPrice}
                      />
                    </div>
                  ) : null}

                  {!!itemPriceAvailability?.price ? (
                    <div className={classes.price}>
                      <FormattedNumber
                        style="currency"
                        currencyDisplay="symbol"
                        currency={itemPriceAvailability?.currency}
                        value={itemPriceAvailability?.price}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className={classes.quantityContainer}>
                {item.type !== CartItemType.Emprove && (
                  <LiquidQuantityInput
                    className={classes.quantityInput}
                    min={1}
                    onChange={setQuantity}
                    size={'small'}
                    value={quantity || 1}
                    disabled={
                      loading || item?.type === CartItemType.Configurator
                    }
                  />
                )}
                {showPricing && !!itemPriceAvailability?.netPrice ? (
                  <div className={classes.netPrice}>
                    <FormattedNumber
                      style="currency"
                      currencyDisplay="symbol"
                      currency={itemPriceAvailability?.currency}
                      value={itemPriceAvailability?.netPrice}
                    />
                  </div>
                ) : (
                  <FormattedMessage {...messages.VIEW_CART_FOR_PRICING} />
                )}
              </div>
            </>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MiniCartItem
